<script setup>
// Merchant admin brand theme picker (per-merchant theming feature).
// Lists only the themes allowed by the super admin (account.settings
// .brand_themes_enabled) and persists the choice via the custom endpoint.
// Hidden entirely when the merchant has only one theme available.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import {
  applyBrandTheme,
  BRAND_THEME_DEFAULT_ACTIVE,
  BRAND_THEME_DEFAULT_ENABLED,
  BRAND_THEME_LEGACY_ENABLED,
} from 'custom/dashboard/helper/brandThemeHelper';
import SectionLayout from 'dashboard/routes/dashboard/settings/account/components/SectionLayout.vue';

const { t } = useI18n();

const THEME_ORDER = ['green', 'default'];

const store = useStore();
const { accountId } = useAccount();
const isSaving = ref(false);

const account = computed(
  () => store.getters['accounts/getAccount'](accountId.value) || {}
);
const settings = computed(() => account.value.settings || {});

const enabledThemes = computed(() => {
  const list = settings.value.brand_themes_enabled;
  const enabled =
    Array.isArray(list) && list.length
      ? list.filter(id => THEME_ORDER.includes(id))
      : settings.value.brand_themes_enabled === undefined
        ? BRAND_THEME_LEGACY_ENABLED
        : BRAND_THEME_DEFAULT_ENABLED;
  return THEME_ORDER.filter(id => enabled.includes(id));
});

const activeTheme = computed(() => {
  const enabled = enabledThemes.value;
  const fallback = enabled.includes(BRAND_THEME_DEFAULT_ACTIVE)
    ? BRAND_THEME_DEFAULT_ACTIVE
    : enabled[0] || BRAND_THEME_DEFAULT_ACTIVE;
  const current = settings.value.brand_theme;
  if (!current) {
    return settings.value.brand_themes_enabled === undefined
      ? 'default'
      : fallback;
  }
  return enabled.includes(current) ? current : fallback;
});

const hasChoice = computed(() => enabledThemes.value.length > 1);

const selectTheme = async theme => {
  if (theme === activeTheme.value || isSaving.value) return;
  isSaving.value = true;
  try {
    await window.axios.put(`/api/v1/accounts/${accountId.value}/brand_theme`, {
      brand_theme: theme,
    });
    // Keep the store copy in sync so reloads/other views reflect the change.
    if (!account.value.settings) account.value.settings = {};
    account.value.settings.brand_theme = theme;
    applyBrandTheme(theme);
    useAlert(t('CUSTOM.BRAND_THEME.SAVED'));
  } catch (error) {
    useAlert(t('CUSTOM.BRAND_THEME.SAVE_FAILED'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <SectionLayout
    v-if="hasChoice"
    :title="$t('CUSTOM.BRAND_THEME.TITLE')"
    :description="$t('CUSTOM.BRAND_THEME.DESCRIPTION')"
  >
    <div class="flex flex-wrap gap-4">
      <button
        v-for="theme in enabledThemes"
        :key="theme"
        type="button"
        :disabled="isSaving"
        class="flex w-40 flex-col gap-3 rounded-xl border p-3 text-left transition-colors disabled:opacity-60"
        :class="
          theme === activeTheme
            ? 'border-n-brand bg-n-alpha-1 outline outline-1 outline-n-brand'
            : 'border-n-weak hover:border-n-slate-6 hover:bg-n-solid-2'
        "
        @click="selectTheme(theme)"
      >
        <!-- Default: upstream blue on light + dark surfaces -->
        <div
          v-if="theme === 'default'"
          class="flex h-14 overflow-hidden rounded-lg border border-n-weak"
        >
          <div
            class="flex flex-1 flex-col items-center justify-end bg-n-solid-2 pb-2"
          >
            <span class="h-2 w-10 rounded-full bg-[#2781F6]" />
          </div>
          <div
            class="flex flex-1 flex-col items-center justify-end bg-[#171717] pb-2"
          >
            <span class="h-2 w-10 rounded-full bg-[#3982F6]" />
          </div>
        </div>

        <!-- Green theme preview -->
        <div
          v-else
          class="flex h-14 overflow-hidden rounded-lg border border-n-weak"
        >
          <div
            class="flex flex-1 flex-col items-center justify-end bg-[#F0FFF4] pb-2"
          >
            <span class="h-2 w-10 rounded-full bg-[#25D366]" />
          </div>
          <div
            class="flex flex-1 flex-col items-center justify-end bg-[#111B21] pb-2"
          >
            <span class="h-2 w-10 rounded-full bg-[#00A884]" />
          </div>
        </div>

        <div class="flex items-center justify-between gap-2">
          <div class="min-w-0">
            <p class="truncate text-sm font-medium text-n-slate-12">
              {{ $t(`CUSTOM.BRAND_THEME.OPTIONS.${theme}`) }}
            </p>
            <p class="truncate text-xs text-n-slate-11">
              {{ $t(`CUSTOM.BRAND_THEME.HINTS.${theme}`) }}
            </p>
          </div>
          <span
            v-if="theme === activeTheme"
            class="i-lucide-check h-4 w-4 shrink-0 text-n-brand"
          />
        </div>
      </button>
    </div>
  </SectionLayout>
</template>
